/**
 * TanStack Query hooks for identity administration. Keys are namespaced
 * "identity/…". Users and roles are small collections and are drained whole:
 * the screens cross-reference them (a user shows its roles by name), which a
 * page at a time cannot do.
 */
import { useQuery } from "@tanstack/react-query";

import {
  listPermissions,
  listRoles,
  listUsers,
  type Permission,
  type Role,
  type User,
} from "./api";

export const identityKeys = {
  users: { all: ["identity/users"] as const, list: () => ["identity/users", "list"] as const },
  roles: { all: ["identity/roles"] as const, list: () => ["identity/roles", "list"] as const },
  permissions: { all: ["identity/permissions"] as const },
};

export function useUsers(enabled = true) {
  return useQuery<User[]>({
    queryKey: identityKeys.users.list(),
    queryFn: ({ signal }) => listUsers({}, signal),
    enabled,
  });
}

export function useRoles(enabled = true) {
  return useQuery<Role[]>({
    queryKey: identityKeys.roles.list(),
    queryFn: ({ signal }) => listRoles({}, signal),
    enabled,
  });
}

export function usePermissions(enabled = true) {
  return useQuery<Permission[]>({
    queryKey: identityKeys.permissions.all,
    queryFn: ({ signal }) => listPermissions(signal),
    enabled,
    // The catalogue changes when the contract does, which is a deploy, not a
    // minute.
    staleTime: 10 * 60 * 1000,
  });
}
